import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ShareChannels from '../../shared/share/ShareChannels';
import ScrollMoreHint from '../../components/ui/ScrollMoreHint';
import { analytics } from '../../shared/analytics';
import { useAuth } from '../auth/context';
import { getStoredSharePassword, sendSharedTripEmail, sharedJourneySuffix } from './api';

/**
 * Trip sharing v1 — the VISITOR's share dialog (reshare, UX decision Paulo
 * 2026-09-14): the same visual shell as the owner's TripShareDialog, minus
 * every control. A visitor can't change HOW the trip is shared; they can
 * pass the link on — see it, copy it, send it by email / WhatsApp / X.
 * Resharing needs no account, so this works for anonymous visitors too:
 * it is the second hop of the viral loop (`role: 'visitor'` on the events).
 *
 * The share message is neutral (the resharer isn't the owner), and when the
 * visitor unlocked a password-protected trip the password line rides along:
 * they were trusted with it, and a reshared link without it would be broken.
 * Reshared from a journey page (`journeyId`), the link opens that journey,
 * as the owner's does (Paulo 2026-10-03). Signed-in visitors can have Jody
 * send it by email; anonymous ones get their email app (ShareChannels).
 */

export interface SharedShareDialogProps {
  token: string;
  tripId: string;
  /** Reshared from a shared journey page: the link lands on this journey. */
  journeyId?: string;
  tripTitle: string;
  source: 'shared_trip' | 'shared_journey';
  onClose: () => void;
}

const SharedShareDialog: React.FC<SharedShareDialogProps> = ({ token, tripId, journeyId, tripTitle, source, onClose }) => {
  const { user } = useAuth();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    analytics.trackTripShareDialogOpened({ trip_id: tripId, source, role: 'visitor' });
  }, [tripId, source]);

  // Escape closes; scroll locks while open (shared dialog conventions).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const shareUrl = `${window.location.origin}/s/${encodeURIComponent(token)}${sharedJourneySuffix(journeyId)}`;
  const password = getStoredSharePassword(token);
  const shareMessage = `Have a look at this trip on Jody: "${tripTitle}". Open it here: ${shareUrl}`
    + (password ? `\nPassword: ${password}` : '');

  const dialog = (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(15, 26, 20, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        ref={scrollRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shared-share-title"
        className="scroll-visible"
        style={{
          width: 'min(460px, 94vw)', maxHeight: '86vh', overflowY: 'auto',
          background: '#fff', borderRadius: 16, padding: '20px 20px 18px',
          boxShadow: '0 24px 56px rgba(15, 23, 42, 0.25)', position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 12, right: 12, padding: '4px 8px', fontSize: 16,
            lineHeight: 1, background: 'transparent', border: 0, color: '#6c7872', cursor: 'pointer',
          }}
        >
          ×
        </button>

        <h3 id="shared-share-title" style={{ fontSize: 18, fontWeight: 700, color: '#16241d', margin: '0 0 2px' }}>
          Share this trip
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6c7872', lineHeight: 1.5 }}>
          {journeyId
            ? <>Anyone with the link can view "{tripTitle}" and all its routes, read-only. The link opens this route.</>
            : <>Anyone with the link can view "{tripTitle}", read-only.</>}
        </p>

        <ShareChannels
          shareText={shareMessage}
          shareUrl={shareUrl}
          password={password}
          emailSubject={`A trip on Jody: ${tripTitle}`}
          shareTitle="Jody"
          sendEmail={user
            ? (to, note) => sendSharedTripEmail(token, { to, message: note, journeyId })
            : undefined}
          onChannel={(channel, detail) => analytics.trackTripShareChannelClicked({ trip_id: tripId, channel, role: 'visitor', ...detail })}
          onDone={onClose}
          copyToastMessage="Paste it anywhere to share this trip."
          copyLinkToastMessage="Paste it anywhere to share this trip."
        />
        <ScrollMoreHint scrollRef={scrollRef} />
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};

export default SharedShareDialog;
